import { useEffect } from "react";
import { WVCEA_EMAIL } from "./help-content";
import { associationPresentation } from "./association-presentation";
import { Link } from "./router";

/**
 * How to have your account deleted, on a page anybody can read.
 *
 * Google requires the address it is given for this to name the app, set out the steps a
 * person takes to ask, and say which data goes and which is kept. The address it had was
 * the privacy policy, which mentions deletion only in passing and gives no steps — the
 * gap that had the app rejected in August and was never actually closed.
 *
 * Reachable without signing in, and without the application installed. Somebody deciding
 * whether to delete an account is often somebody who cannot get into it.
 */
export function DeleteAccountPage() {
  const presentation = associationPresentation();
  useEffect(() => {
    document.title = "Delete your account · WVCEA Connect";
  }, []);

  // The steps below are WVCEA's. Another association on this platform publishes its own, and
  // telling its teachers to write to WVCEA would be worse than telling them nothing.
  if (!presentation.wvcea) return (
    <div className="page privacy-page">
      <div className="page-intro">
        <h1 data-page-heading tabIndex={-1}>Delete your account</h1>
        <p>{presentation.appName} has not published its account deletion details yet. Ask the association that runs it.</p>
      </div>
    </div>
  );

  return (
    <div className="page privacy-page">
      <div className="page-intro">
        <h1 data-page-heading tabIndex={-1}>Delete your account</h1>
        <p>How to have your WVCEA Connect account removed, and what happens to your information when it is.</p>
      </div>

      <div className="privacy-list">
        <section>
          <h2>Which app this is</h2>
          <p>
            WVCEA Connect, published by VerityWord for the West Virginia Christian Education
            Association. It is the app teachers use at the association's Educators' Convention.
          </p>
        </section>

        <section>
          <h2>Deleting it yourself, in the app</h2>
          <p>
            This is the quickest way and it takes effect at once.
          </p>
          <ol className="delete-steps">
            <li>Open WVCEA Connect and sign in.</li>
            <li>Tap <strong>My WVCEA</strong> at the bottom of the screen.</li>
            <li>Tap <strong>Account</strong>.</li>
            <li>Scroll to the bottom and tap <strong>Delete your account</strong>.</li>
            <li>
              Read what the screen says will be removed, then tap <strong>Delete my account</strong>.
              You can stop at this point by tapping <strong>Keep my account</strong> instead.
            </li>
          </ol>
          <p>
            You are signed out immediately and your password stops working. Nothing is held for a
            grace period and nothing has to be confirmed by email.
          </p>
        </section>

        <section>
          <h2>Asking us to do it instead</h2>
          <p>
            If you cannot sign in, or you would rather not do it yourself, write to{" "}
            <a className="help-action" href={`mailto:${WVCEA_EMAIL}?subject=Delete%20my%20WVCEA%20Connect%20account`}>
              {WVCEA_EMAIL}<b aria-hidden="true">›</b>
            </a>{" "}
            from the email address your school registered, and ask for your account to be deleted.
            We will confirm when it is done. Allow up to thirty days, though it is normally the
            same week.
          </p>
        </section>

        <section>
          <h2>What is deleted</h2>
          <p>
            Everything you made in the app, permanently and without a copy kept:
          </p>
          <ul>
            <li>Your private notes, and every earlier version of them.</li>
            <li>Your learning records and convention wrap-up, and their earlier versions.</li>
            <li>Your reviews of the convention, and their earlier versions.</li>
            <li>Any session you recorded, and the transcripts made from it.</li>
            <li>Your sign-in itself — your password, and the devices you set up to sign in with Touch ID or Face ID.</li>
          </ul>
        </section>

        <section>
          <h2>What is kept, and why</h2>
          <p>
            Deleting your account does not erase the association's record that you attended.
            Two things survive it, and they belong to WVCEA rather than to your account:
          </p>
          <ul>
            <li>
              <strong>Your registration</strong> — that your school registered you for a convention,
              and that you were there.
            </li>
            <li>
              <strong>Your continuing education record</strong> — the contact hours you earned, kept
              under your name. An anonymous record of six contact hours would be no use to anybody,
              least of all to you when your school or AACS asks what you completed.
            </li>
          </ul>
          <p>
            These are kept for as long as the association keeps its convention records. So
            "delete my account" here means your account and everything in it goes; it does not
            mean the association forgets you attended.
          </p>
          <p>
            If a recording of yours was chosen as WVCEA's official copy of a session, it stays in
            the association's archive for everyone who attended that session, as the{" "}
            <Link href="/privacy">privacy policy</Link> explains.
          </p>
        </section>

        <section>
          <h2>Coming back afterwards</h2>
          <p>
            You can. Your registration stays where it is, so signing up again with the same email
            address finds it and lets you set a new password against the record you already had.
          </p>
        </section>

        <section>
          <h2>Questions</h2>
          <p>
            <a className="help-action" href={`mailto:${WVCEA_EMAIL}`}>{WVCEA_EMAIL}<b aria-hidden="true">›</b></a>
          </p>
          <p><Link href="/privacy">Read the full privacy policy</Link></p>
        </section>
      </div>
    </div>
  );
}
